import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of, switchMap } from "rxjs";
import { catchError, map, withLatestFrom } from "rxjs/operators";

import { MatDialog } from "@angular/material/dialog";
import { MetacomService } from "@core/services/metacom.service";
import { Store } from "@ngrx/store";
import { ErrorDialogComponent } from "@shared/components/error-dialog/error-dialog.component";
import { hasNestedEmptyValues } from "@shared/utils/objects.util";
import { metacomActions } from "./actions";
import { selectMetacomDetails } from "./selectors";
import { Router } from "@angular/router";
import { handleConnectionStatus } from "@shared/utils/metacom-response.util";
import {
  MetacomConnectionResponse,
  MetacomMomentResponse,
} from "@core/models/api.response";

export const storeMetacomDetailsEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(metacomActions.setMetacomDetails),
      switchMap(
        (action: ReturnType<typeof metacomActions.setMetacomDetails>) => {
          const data = {
            authHash: action.authHash,
            authEndPoint: action.authEndPoint,
            status: action.status,
            ip: action.ip,
            mac: action.mac,
          };
          if (!hasNestedEmptyValues(data)) {
            return [
              metacomActions.setMetacomDetailsSuccess({ metacomDetails: data }),
            ];
          } else {
            return [metacomActions.setMetacomDetailsFailed({})];
          }
        }
      )
    );
  },
  { functional: true }
);

export const storeMetacomDetailsFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(metacomActions.setMetacomDetailsFailed),
      map(() => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: "Could not find the metacom details",
          },
        });
      })
    );
  },
  { dispatch: false, functional: true }
);

export const metacomConnectionEffect = createEffect(
  (
    actions$ = inject(Actions),
    metacomService = inject(MetacomService),
    store = inject(Store),
    router = inject(Router)
  ) => {
    return actions$.pipe(
      ofType(metacomActions.requestMetacomConnection),
      withLatestFrom(store.select(selectMetacomDetails)), // Combine with store data
      switchMap(([action, metacomDetails]) => {
        return metacomService.setupMetacomConnection(metacomDetails).pipe(
          map((response: MetacomConnectionResponse) => {
            if (response?.connected) {
              return metacomActions.requestMetacomConnectionSuccess(response);
            } else {
              return handleConnectionStatus(router, response.message);
            }
          }),
          catchError((error) => {
            return of(handleConnectionStatus(router, error.message));
          })
        );
      })
    );
  },
  { functional: true }
);

export const metacomConnectSuccessEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(metacomActions.requestMetacomConnectionSuccess),
      map(() => {
        return metacomActions.requestMetacomMoment();
      })
    );
  },
  { functional: true }
);

export const metacomConnectFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(metacomActions.requestMetacomConnectionFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage:
              data.message ??
              "We could not establish a connection, please try again.",
          },
        });
      })
    );
  },
  { dispatch: false, functional: true }
);

export const metacomMomentEffect = createEffect(
  (
    actions$ = inject(Actions),
    metacomService = inject(MetacomService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(metacomActions.requestMetacomMoment),
      withLatestFrom(store.select(selectMetacomDetails)), // Combine with store data
      switchMap(([action, metacomDetails]) => {
        return metacomService.createMetacomMoment().pipe(
          map((response: MetacomMomentResponse) => {
            return metacomActions.requestMetacomMomentSuccess(response);
          }),
          catchError((error) => {
            return of(
              metacomActions.requestMetacomMomentFailed({
                connected: false,
                message:
                  "We weren't able to finalise the connection, please try again",
              })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const metacomMomentFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(metacomActions.requestMetacomMomentFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },
  { dispatch: false, functional: true }
);
